
import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, Mail, Hash, Lock, Loader2, ShieldCheck, Check, AlertCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/customSupabaseClient';

// Initialize Stripe with the provided Public Key
const stripePromise = loadStripe('pk_live_51SaOUm92nzFFppEgT5M7ex72CCdD4eFDMtysHxBHJkMXSW4vblUhKVTG9FN5zt9uB98rx4GHJv6FKM3ddKsuVaOK008V9lpkIQ');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#0A1929',
      fontFamily: '"Segoe UI", sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

// Function to trigger Supabase Edge Function for email delivery
const sendOrderEmailToSupport = async (orderData) => {
  console.log('🚀 Invoking Supabase Edge Function: send-order-email...');
  
  const { data, error } = await supabase.functions.invoke('send-order-email', {
    body: orderData
  });

  if (error) {
    console.error('❌ Error sending email via Edge Function:', error);
    // We throw the error to be caught by the calling function's try/catch block
    throw new Error(error.message || 'Failed to send notification email');
  }

  console.log('✅ Email successfully queued via Edge Function:', data);
  return data;
};

const CheckoutForm = ({ onPaymentSuccess, price }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Form state for VIN and Email
  const [vin, setVin] = useState('');
  const [email, setEmail] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // Validate inputs before processing payment
    if (!vin || vin.length !== 17) {
      setError('Please enter a valid 17-character VIN.');
      return;
    }
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setProcessing(true);
    setError(null);

    const card = elements.getElement(CardElement);

    if (card == null) {
      return;
    }

    // Create PaymentMethod with billing details including email
    const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card,
      billing_details: {
        email: email,
      },
      metadata: {
        vin: vin
      }
    });

    if (stripeError) {
      setError(stripeError.message);
      setProcessing(false);
    } else {
      console.log('[PaymentMethod Created]', paymentMethod);
      
      try {
        // Send email notification to support team via Supabase Edge Function
        await sendOrderEmailToSupport({
          vin,
          email,
          paymentId: paymentMethod.id,
          price
        });
        
        setProcessing(false);
        onPaymentSuccess({ vin, email });
      } catch (err) {
        console.error('Failed to send support email:', err);
        // Even if email fails, payment succeeded. We inform the user but log the critical error.
        // Ideally, we might want to retry or save to database as backup.
        setError('Order processed, but notification system is delayed. Please screenshot this page.');
        
        // For now, we still show success to user if it's just an email service glitch, 
        // but in strict mode we might want to show the error. 
        // Let's proceed to success but maybe with a warning if needed.
        // However, since we threw an error, the flow stops here. 
        // Let's allow success but show a toast error? 
        // Decided: If email fails, it's critical for manual fulfillment. Show error in form.
        setProcessing(false);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-4">
         <div className="flex justify-between items-center">
           <span className="font-bold text-[#0A1929]">CARFAX Report</span>
           <span className="font-bold text-xl text-[#FF6B35]">{price}</span>
         </div>
         <div className="flex items-center gap-2 text-sm text-blue-700 mt-2">
           <ShieldCheck size={16} />
           <span>Verified secure transaction</span>
         </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="vin" className="text-[#0A1929] font-semibold">VIN</Label> {/* Changed from "Vehicle VIN" to "VIN" */}
          <div className="relative">
            <Hash className="absolute left-3 top-3 text-gray-400" size={18} />
            <Input
              id="vin"
              placeholder="Enter 17-character VIN"
              value={vin}
              onChange={(e) => setVin(e.target.value.toUpperCase())}
              maxLength={17}
              className="pl-10 border-gray-300 focus:border-[#FF6B35] focus:ring-[#FF6B35] font-mono uppercase placeholder:normal-case"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-[#0A1929] font-semibold">Delivery Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 text-gray-400" size={18} />
            <Input
              id="email"
              type="email"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 border-gray-300 focus:border-[#FF6B35] focus:ring-[#FF6B35]"
              required
            />
          </div>
          <p className="text-xs text-gray-500">Report will be sent to this email instantly.</p>
        </div>

        <div className="space-y-2 pt-2">
          <Label htmlFor="card-element" className="text-[#0A1929] font-semibold">Card Details</Label>
          <div className="p-3 border rounded-lg bg-white border-gray-300 focus-within:border-[#FF6B35] focus-within:ring-1 focus-within:ring-[#FF6B35] transition-all shadow-sm">
            <CardElement id="card-element" options={CARD_ELEMENT_OPTIONS} />
          </div>
        </div>
      </div>

      {error && (
        <div className="text-red-600 text-sm flex items-center gap-2 bg-red-50 p-3 rounded-md border border-red-100">
          <AlertCircle size={16} />
          <span className="font-medium">{error}</span>
        </div>
      )}

      <Button 
        type="submit" 
        disabled={!stripe || processing}
        className="w-full bg-[#FF6B35] hover:bg-[#e55a2b] text-white py-6 text-lg font-bold shadow-md hover:shadow-lg transition-all mt-2"
      >
        {processing ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Processing Order...
          </>
        ) : (
          `Pay & Get Report ${price}`
        )}
      </Button>
      
      <div className="flex justify-center items-center gap-2 opacity-60 pt-2">
        <Lock size={12} className="text-gray-500" />
        <span className="text-xs text-gray-500">Secure 256-bit encrypted payment</span> 
      </div>
    </form>
  );
};

const PaymentDialog = ({ open, onOpenChange }) => {
  const [successData, setSuccessData] = useState(null); // { vin, email } or null
  const { toast } = useToast();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
        setSuccessData(null);
    }
  }, [open]);

  const handlePaymentSuccess = (data) => {
    setSuccessData(data);
    toast({
        title: "Order Received! 🎉",
        description: `Our team has been notified. Report for ${data.vin} will be sent shortly.`,
        duration: 5000,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white p-0 overflow-hidden gap-0 border-0 shadow-2xl">
        <div className="bg-[#0A1929] p-6 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-2">
              {!successData ? (
                <>
                    <CreditCard className="h-6 w-6 text-[#FF6B35]" />
                    Secure Checkout
                </>
              ) : (
                <>
                    <Check className="h-6 w-6 text-green-400" />
                    Order Confirmed
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-gray-300 mt-2">
              {!successData
                ? 'Enter vehicle and payment details below.' 
                : 'Your transaction was successful.'}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6">
            <Elements stripe={stripePromise}>
                {!successData ? (
                    <CheckoutForm onPaymentSuccess={handlePaymentSuccess} price="$34.99" />
                ) : (
                    <div className="text-center py-6 space-y-4 animate-in fade-in zoom-in duration-500">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                            <Check className="w-8 h-8 text-green-600" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-[#0A1929] mb-2">Order Received!</h3>
                            <p className="text-gray-600">
                                The request for <span className="font-mono font-bold text-black">{successData.vin}</span> has been sent to our support team.
                            </p>
                            <p className="text-gray-600 mt-2">
                                We will manually generate the report and email it to <span className="font-bold text-black">{successData.email}</span> within 15-30 minutes.
                            </p>
                            <p className="text-sm text-gray-500 mt-4">Please check your inbox (and spam folder) shortly.</p>
                        </div>
                        <Button 
                            onClick={() => onOpenChange(false)}
                            className="w-full bg-[#0A1929] text-white mt-4"
                        >
                            Close
                        </Button>
                    </div>
                )}
            </Elements>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDialog;
